// MARK://dhk8/admin-shell/pages/modules/UnitsCatalogPage
// Catálogo de Unidades — gestión central de las unidades TIPADAS del tenant
// (Gustavo 2026-06-23: nada de texto libre). Tres bloques:
//   · Favoritos     — claves SAT recurrentes (tenantUnits.isFavorite); ★ para quitar.
//   · Personalizadas — unidades custom fuera del SAT (tenantUnits.isCustom); editar/borrar.
//   · Catálogo SAT  — c_ClaveUnidad (2,417 claves, lazy-loaded) buscable, con ★ por fila
//                     para marcar/quitar favorito (setUnitFavorite).
// Datos por el grafo (catalog-pricing/TenantUnitService, tenant del JWT, RLS) vía
// lib/clients/units. El catálogo SAT se carga perezosamente (chunk aparte) al entrar.
// DS NintaiGO: DataTable + FilterBar + SummaryStrip + SectionedSheet + StatusPill;
// curvas/tokens, sin border-left.
import { useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, Star, Ruler, ArrowRight } from 'lucide-react'

import * as units from '../../lib/clients/units.js'
import UnitPicker, { loadSatCatalog, useSatCatalog, unitShort, type SatUnit, UNITS } from '../../components/UnitPicker.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill from '../../components/StatusPill.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

function norm(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

// Unidad BASE sugerida por dimensión (GR/ML/PZA). El motor convierte transitivo vía
// la base, así que basta declarar el factor de la unidad a su base. Se deriva del
// `group` de las unidades curadas; para claves SAT/custom desconocidas → PZA.
const BASE_BY_GROUP: Record<string, string> = { Peso: 'GR', Volumen: 'ML', Conteo: 'PZA' }
function suggestBaseUnit(fromUnit: string): string {
  const u = UNITS.find((x) => x.code === fromUnit || x.sat === fromUnit)
  return (u && BASE_BY_GROUP[u.group]) || 'PZA'
}

interface CustomForm { label: string; symbol: string; satClave: string; eqFactor: string; eqBase: string }
const emptyForm = (): CustomForm => ({ label: '', symbol: '', satClave: '', eqFactor: '', eqBase: '' })

// Form de equivalencia: factor en TEXTO (input decimal plano, sin steppers).
interface ConvForm { fromUnit: string; factor: string; toUnit: string }
const emptyConvForm = (): ConvForm => ({ fromUnit: '', factor: '', toUnit: '' })
// Parser tolerante: acepta coma o punto decimal; devuelve null si no es número > 0.
function parseFactor(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

// Fila de Favoritos: los básicos (las Comunes del dropdown) + los marcados del SAT.
type FavRow = { id: string; unitKey: string; label: string; symbol: string | null; satClave: string | null; base: boolean }
const SAT_PAGE_SIZE = 50

export default function UnitsCatalogPage() {
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => units.listTenantUnits(s))
  const convList = useAsync((s) => units.listUnitConversions(s))
  const sat = useSatCatalog()

  // Catálogo SAT (lazy) — se carga al entrar a la página.
  const [satLoading, setSatLoading] = useState(false)
  const [satError, setSatError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setSatLoading(true)
    loadSatCatalog()
      .then(() => { if (live) { setSatLoading(false); setSatError(null) } })
      .catch((e) => { if (live) { setSatLoading(false); setSatError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo SAT.') } })
    return () => { live = false }
  }, [])

  // Alta/edición de unidad personalizada.
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<units.TenantUnit | null>(null)
  const [form, setForm] = useState<CustomForm>(emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  // SAT browse: búsqueda + orden + paginación.
  const [satSearch, setSatSearch] = useState('')
  const [satSort, setSatSort] = useState<SortState | null>({ key: 'clave', dir: 'asc' })
  const [satPage, setSatPage] = useState(1)

  // Equivalencias (unit_conversions): alta + estado del sheet.
  const [convOpen, setConvOpen] = useState(false)
  const [convForm, setConvForm] = useState<ConvForm>(emptyConvForm())
  const [convError, setConvError] = useState<string | null>(null)
  const [convSaving, setConvSaving] = useState(false)

  const tenantUnits = list.data ?? []
  const favorites = useMemo(() => tenantUnits.filter((u) => u.isFavorite), [tenantUnits])
  const customs = useMemo(() => tenantUnits.filter((u) => u.isCustom), [tenantUnits])
  const favKeys = useMemo(() => new Set(favorites.map((u) => u.unitKey)), [favorites])

  // Favoritos VISIBLES = los básicos (Comunes, SIEMPRE) + lo que el tenant marcó del SAT
  // (dedup contra los básicos; las custom viven en su propia sección).
  const baseSatKeys = useMemo(() => new Set(UNITS.flatMap((u) => [u.code, ...(u.sat ? [u.sat] : [])])), [])
  const favRows = useMemo<FavRow[]>(() => {
    const base: FavRow[] = UNITS.map((u) => ({ id: 'base:' + u.code, unitKey: u.code, label: u.label, symbol: u.short, satClave: u.sat ?? null, base: true }))
    const extra: FavRow[] = favorites
      .filter((u) => !u.isCustom && !baseSatKeys.has(u.unitKey) && !(u.satClave != null && baseSatKeys.has(u.satClave)))
      .map((u) => ({ id: u.id, unitKey: u.unitKey, label: u.label, symbol: u.symbol, satClave: u.satClave, base: false }))
    return [...base, ...extra]
  }, [favorites, baseSatKeys])

  function openCreate() { setForm(emptyForm()); setFormError(null); setCreating(true) }
  function openEdit(u: units.TenantUnit) {
    // Edición no toca equivalencias (se gestionan en su sección) → eq* vacíos.
    setForm({ label: u.label, symbol: u.symbol ?? '', satClave: u.satClave ?? '', eqFactor: '', eqBase: '' })
    setFormError(null); setEditing(u)
  }

  async function saveCreate() {
    setFormError(null)
    if (!form.label.trim()) { setFormError('El nombre es obligatorio.'); return }
    // Equivalencia OPCIONAL: si capturan factor, debe ser válido y tener unidad base.
    const eqRaw = form.eqFactor.trim()
    let eqFactor: number | null = null
    if (eqRaw) {
      eqFactor = parseFactor(eqRaw)
      if (eqFactor == null) { setFormError('El factor de la equivalencia debe ser un número mayor que 0.'); return }
      if (!form.eqBase) { setFormError('Elige la unidad base de la equivalencia.'); return }
    }
    setSaving(true)
    try {
      const created = await units.createCustomUnit({
        label: form.label.trim(),
        ...(form.symbol.trim() ? { symbol: form.symbol.trim() } : {}),
        ...(form.satClave.trim() ? { satClave: form.satClave.trim() } : {}),
      })
      // Tras crear la unidad, si pidieron equivalencia: 1 <unidad> = factor <base>.
      if (eqFactor != null && form.eqBase) {
        try {
          await units.upsertUnitConversion({ fromUnit: created.unitKey, toUnit: form.eqBase, factor: eqFactor })
          convList.reload()
        } catch (e) {
          setCreating(false); list.reload()
          toast(`Unidad creada, pero no se pudo guardar la equivalencia: ${e instanceof Error ? e.message : 'error'}`, 'warn')
          return
        }
      }
      setCreating(false); list.reload()
      toast(eqFactor != null ? 'Unidad y equivalencia creadas' : 'Unidad creada', 'success')
    } catch (e) { setFormError(e instanceof Error ? e.message : 'No se pudo crear la unidad.') }
    finally { setSaving(false) }
  }

  async function saveEdit() {
    if (!editing) return
    setFormError(null)
    if (!form.label.trim()) { setFormError('El nombre es obligatorio.'); return }
    setSaving(true)
    try {
      await units.updateCustomUnit(editing.id, {
        label: form.label.trim(),
        symbol: form.symbol.trim() ? form.symbol.trim() : null,
        satClave: form.satClave.trim() ? form.satClave.trim() : null,
      })
      setEditing(null); list.reload()
      toast('Unidad actualizada', 'success')
    } catch (e) { setFormError(e instanceof Error ? e.message : 'No se pudo actualizar la unidad.') }
    finally { setSaving(false) }
  }

  async function removeCustom(u: units.TenantUnit) {
    const ok = await confirm({
      title: '¿Eliminar unidad personalizada?',
      message: `Se eliminará "${u.label}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try { await units.deleteCustomUnit(u.id); list.reload(); toast('Unidad eliminada', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar la unidad.', 'danger') }
  }

  // ── Equivalencias ───────────────────────────────────────────────────────────
  // Sin `c` = nueva; con `c` = editar (re-upsert del mismo par fromUnit→toUnit con
  // nuevo factor; el backend hace upsert por ese par).
  function openConv(c?: units.UnitConversion) {
    setConvForm(c ? { fromUnit: c.fromUnit, factor: String(c.factor), toUnit: c.toUnit } : emptyConvForm())
    setConvError(null); setConvOpen(true)
  }
  function setConvFrom(fromUnit: string) {
    // Al elegir la unidad de origen, prerellena la base sugerida de su dimensión
    // (si el usuario no eligió una destino aún).
    setConvForm((f) => ({ ...f, fromUnit, toUnit: f.toUnit || suggestBaseUnit(fromUnit) }))
  }
  async function saveConv() {
    setConvError(null)
    if (!convForm.fromUnit) { setConvError('Elige la unidad de origen.'); return }
    if (!convForm.toUnit) { setConvError('Elige la unidad base/destino.'); return }
    if (convForm.fromUnit === convForm.toUnit) { setConvError('La unidad de origen y la base deben ser distintas.'); return }
    const factor = parseFactor(convForm.factor)
    if (factor == null) { setConvError('El factor debe ser un número mayor que 0.'); return }
    setConvSaving(true)
    try {
      await units.upsertUnitConversion({ fromUnit: convForm.fromUnit, toUnit: convForm.toUnit, factor })
      setConvOpen(false); convList.reload()
      toast('Equivalencia guardada', 'success')
    } catch (e) { setConvError(e instanceof Error ? e.message : 'No se pudo guardar la equivalencia.') }
    finally { setConvSaving(false) }
  }
  async function removeConv(c: units.UnitConversion) {
    const ok = await confirm({
      title: '¿Eliminar equivalencia?',
      message: `Se eliminará "1 ${unitShort(c.fromUnit)} = ${c.factor} ${unitShort(c.toUnit)}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try { await units.deleteUnitConversion(c.id); convList.reload(); toast('Equivalencia eliminada', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar la equivalencia.', 'danger') }
  }

  async function unstar(u: { unitKey: string; label: string; symbol: string | null; satClave: string | null }) {
    setBusyKey(u.unitKey)
    try {
      await units.setUnitFavorite({ unitKey: u.unitKey, label: u.label, symbol: u.symbol, satClave: u.satClave, favorite: false })
      list.reload(); toast('Quitada de favoritos', 'success')
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo actualizar el favorito.', 'danger') }
    finally { setBusyKey(null) }
  }

  async function toggleSatFavorite(u: SatUnit) {
    const isFav = favKeys.has(u.clave)
    setBusyKey(u.clave)
    try {
      await units.setUnitFavorite({ unitKey: u.clave, label: u.nombre, symbol: u.simbolo, satClave: u.clave, favorite: !isFav })
      list.reload(); toast(isFav ? 'Quitada de favoritos' : 'Agregada a favoritos', 'success')
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo actualizar el favorito.', 'danger') }
    finally { setBusyKey(null) }
  }

  // ── SAT browse: filtrado + orden client-side sobre las 2,417 claves, PAGINADO ──
  const satFiltered = useMemo(() => {
    const all = sat ?? []
    const q = norm(satSearch.trim())
    let out = q
      ? all.filter((u) => norm(u.clave).includes(q) || norm(u.nombre).includes(q) || (!!u.simbolo && norm(u.simbolo).includes(q)))
      : all
    if (satSort) {
      const dir = satSort.dir === 'asc' ? 1 : -1
      out = [...out].sort((a, b) => {
        const av = satSort.key === 'nombre' ? a.nombre : a.clave
        const bv = satSort.key === 'nombre' ? b.nombre : b.clave
        return av.localeCompare(bv, 'es-MX') * dir
      })
    }
    return out
  }, [sat, satSearch, satSort])
  const satPageRows = useMemo(
    () => satFiltered.slice((satPage - 1) * SAT_PAGE_SIZE, satPage * SAT_PAGE_SIZE),
    [satFiltered, satPage],
  )

  // ── columnas ──
  const favCols: DataTableColumn<FavRow>[] = [
    { key: 'label', header: 'Unidad', render: (u) => <span><Star size={13} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--pos-warning, #d4a017)' }} />{u.label}</span> },
    { key: 'symbol', header: 'Símbolo', render: (u) => <span className="adm-mono">{u.symbol ?? '—'}</span> },
    { key: 'satClave', header: 'Clave SAT', render: (u) => <span className="adm-mono">{u.satClave ?? u.unitKey}</span> },
    { key: 'origen', header: 'Origen', render: (u) => <StatusPill tone={u.base ? 'neutral' : 'warn'} label={u.base ? 'Básico' : 'SAT'} /> },
    {
      key: 'acciones', header: '', align: 'right', width: '1%',
      render: (u) => u.base ? <span className="adm-muted" style={{ fontSize: 11 }}>fijo</span> : (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => void unstar(u)} disabled={busyKey === u.unitKey} aria-label="Quitar de favoritos" title="Quitar de favoritos"><Star size={15} /></button>
        </div>
      ),
    },
  ]

  const customCols: DataTableColumn<units.TenantUnit>[] = [
    { key: 'label', header: 'Unidad', render: (u) => <span><Ruler size={13} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.6 }} />{u.label}</span> },
    { key: 'symbol', header: 'Símbolo', render: (u) => <span className="adm-mono">{u.symbol ?? '—'}</span> },
    { key: 'satClave', header: 'Clave SAT equiv.', render: (u) => <span className="adm-mono">{u.satClave ?? '—'}</span> },
    { key: 'fav', header: 'Favorito', render: (u) => (u.isFavorite ? <StatusPill tone="warn" label="★ favorito" /> : '—') },
    {
      key: 'acciones', header: '', align: 'right', width: '1%',
      render: (u) => (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEdit(u)} aria-label="Editar" title="Editar"><Pencil size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => void removeCustom(u)} aria-label="Eliminar" title="Eliminar"><Trash2 size={15} /></button>
        </div>
      ),
    },
  ]

  const satCols: DataTableColumn<SatUnit>[] = [
    { key: 'clave', header: 'Clave', sortable: true, width: '12%', render: (u) => <span className="adm-mono">{u.clave}</span> },
    { key: 'nombre', header: 'Nombre', sortable: true, render: (u) => u.nombre },
    { key: 'simbolo', header: 'Símbolo', render: (u) => <span className="adm-mono">{u.simbolo ?? '—'}</span> },
    {
      key: 'fav', header: '', align: 'right', width: '1%',
      render: (u) => {
        const isFav = favKeys.has(u.clave)
        return (
          <button
            className={`adm-btn adm-btn--ghost adm-btn--sm${isFav ? '' : ''}`}
            onClick={() => void toggleSatFavorite(u)}
            disabled={busyKey === u.clave}
            aria-label={isFav ? 'Quitar de favoritos' : 'Marcar favorito'}
            title={isFav ? 'Quitar de favoritos' : 'Marcar favorito'}
            style={isFav ? { color: 'var(--pos-warning, #d4a017)' } : undefined}
          >
            <Star size={15} />
          </button>
        )
      },
    },
  ]

  // Equivalencias: Origen (global/tenant) · De → A (bonito vía unitShort) · Factor.
  const convCols: DataTableColumn<units.UnitConversion>[] = [
    { key: 'origen', header: 'Origen', width: '1%', render: (c) => <StatusPill tone={c.global ? 'neutral' : 'info'} label={c.global ? 'Global' : 'Tenant'} /> },
    {
      key: 'conv', header: 'Equivalencia',
      render: (c) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>1 <span className="adm-mono">{unitShort(c.fromUnit)}</span></span>
          <ArrowRight size={13} aria-hidden style={{ opacity: 0.5 }} />
          <span><span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.factor}</span> <span className="adm-mono">{unitShort(c.toUnit)}</span></span>
        </span>
      ),
    },
    { key: 'factor', header: 'Factor', align: 'right', render: (c) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.factor}</span> },
    {
      key: 'acciones', header: '', align: 'right', width: '1%',
      render: (c) => c.global ? <span className="adm-muted" style={{ fontSize: 11 }}>fijo</span> : (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openConv(c)} aria-label="Editar" title="Editar"><Pencil size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => void removeConv(c)} aria-label="Eliminar" title="Eliminar"><Trash2 size={15} /></button>
        </div>
      ),
    },
  ]

  // `withEquivalence` solo en ALTA: ofrece declarar "1 <unidad> = factor <base>"
  // de una vez (opcional). En edición no aplica (las equivalencias se gestionan en
  // su propia sección).
  const renderFields = (withEquivalence: boolean) => (
    <>
      {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
      <FormSection title="Datos de la unidad">
        <label className="adm-field">
          <span className="adm-field__label">Nombre</span>
          <input className="adm-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Charola, rejilla, atado…" />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Símbolo (opcional)</span>
          <input className="adm-input" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="ch, rej…" />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Clave SAT equivalente (opcional)</span>
          <input className="adm-input adm-mono" value={form.satClave} onChange={(e) => setForm({ ...form, satClave: e.target.value })} placeholder="ej. H87" />
          <span className="adm-field__hint">Si esta unidad mapea a una clave c_ClaveUnidad para CFDI.</span>
        </label>
      </FormSection>
      {withEquivalence && (
        <FormSection title="Equivalencia (opcional)">
          <p className="adm-muted" style={{ margin: '0 0 4px', fontSize: 'var(--pos-text-xs, 12px)' }}>
            Cuánto vale esta unidad en una unidad base (GR/ML/PZA). El motor convierte el resto. Puedes dejarlo en blanco y definirlo después.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--pos-space-2, 8px)', flexWrap: 'wrap' }}>
            <span className="adm-muted" style={{ paddingBottom: 12, whiteSpace: 'nowrap' }}>1 {form.label.trim() || 'unidad'} =</span>
            <label className="adm-field" style={{ width: 120 }}>
              <span className="adm-field__label">Factor</span>
              <input
                className="adm-input adm-mono"
                type="text"
                inputMode="decimal"
                value={form.eqFactor}
                onChange={(e) => setForm({ ...form, eqFactor: e.target.value })}
                placeholder="ej. 12"
              />
            </label>
            <div className="adm-field" style={{ flex: 1, minWidth: 200 }}>
              <span className="adm-field__label">Unidad base</span>
              <UnitPicker bare value={form.eqBase} onChange={(v) => setForm({ ...form, eqBase: v })} />
            </div>
          </div>
        </FormSection>
      )}
    </>
  )

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Unidades</h1>
          <p className="adm-page__sub">Unidades tipadas del negocio: favoritos del catálogo SAT y unidades personalizadas. Sin texto libre.</p>
        </div>
      </div>

      <div style={{ marginBottom: 'var(--pos-space-3, 12px)' }}>
        <SummaryStrip items={[
          { label: 'Favoritos', value: favorites.length, tone: 'ok' },
          { label: 'Personalizadas', value: customs.length, tone: 'info' },
          { label: 'Equivalencias', value: convList.data ? convList.data.length : (convList.loading ? '…' : 0) },
          { label: 'Catálogo SAT', value: sat ? sat.length : (satLoading ? '…' : 0) },
        ]} />
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <>
          {/* Favoritos = básicos (Comunes) + lo marcado del SAT */}
          <section style={{ marginBottom: 'var(--pos-space-5, 20px)' }}>
            <h2 className="adm-section-title">Favoritos</h2>
            <p className="adm-muted" style={{ marginTop: -4, marginBottom: 8, fontSize: 'var(--pos-text-xs, 12px)' }}>
              Las unidades básicas siempre están aquí (fijas). Marca ★ en el catálogo SAT para agregar más.
            </p>
            <DataTable
              columns={favCols}
              rows={favRows}
              getRowKey={(u) => u.id}
              zebra
              emptyText="Sin favoritos."
            />
          </section>

          {/* Personalizadas */}
          <section style={{ marginBottom: 'var(--pos-space-5, 20px)' }}>
            <h2 className="adm-section-title">Personalizadas</h2>
            <DataTable
              columns={customCols}
              rows={customs}
              getRowKey={(u) => u.id}
              zebra
              emptyText="Sin unidades personalizadas. Crea una para lo que no existe en el SAT."
              toolbar={
                <FilterBar
                  actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}><Plus size={16} /> Nueva unidad</button>}
                />
              }
            />
          </section>

          {/* Equivalencias (unit_conversions): globales read-only + del tenant editables */}
          <section style={{ marginBottom: 'var(--pos-space-5, 20px)' }}>
            <h2 className="adm-section-title">Equivalencias</h2>
            <p className="adm-muted" style={{ marginTop: -4, marginBottom: 8, fontSize: 'var(--pos-text-xs, 12px)' }}>
              Define cuánto vale una unidad en su unidad base (GR/ML/PZA); el motor convierte el resto.
              Las globales son fijas; las de tu negocio puedes editarlas o eliminarlas.
            </p>
            {convList.error
              ? asyncState(convList.loading, convList.error, convList.reload)
              : (
                <DataTable
                  columns={convCols}
                  rows={convList.data ?? []}
                  getRowKey={(c) => c.id}
                  loading={convList.loading && !convList.data}
                  zebra
                  emptyText="Sin equivalencias del negocio todavía. Crea una para tus unidades custom o del SAT."
                  toolbar={
                    <FilterBar
                      actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => openConv()}><Plus size={16} /> Nueva equivalencia</button>}
                    />
                  }
                />
              )}
          </section>

          {/* Catálogo SAT (lazy) */}
          <section>
            <h2 className="adm-section-title">Catálogo SAT · c_ClaveUnidad</h2>
            {satError && <div className="adm-banner adm-banner--danger" role="alert">{satError}</div>}
            <DataTable
              columns={satCols}
              rows={satPageRows}
              getRowKey={(u) => u.clave}
              zebra
              sort={satSort}
              onSortChange={(s) => { setSatSort(s); setSatPage(1) }}
              page={satPage}
              pageSize={SAT_PAGE_SIZE}
              total={satFiltered.length}
              onPageChange={setSatPage}
              emptyText={satLoading ? 'Cargando catálogo SAT…' : (satSearch ? 'Sin claves que coincidan.' : 'Catálogo no disponible.')}
              toolbar={
                <FilterBar
                  search={satSearch}
                  onSearch={(v) => { setSatSearch(v); setSatPage(1) }}
                  searchPlaceholder="Buscar por clave, nombre o símbolo… (2,417 claves)"
                />
              }
            />
          </section>
        </>
      )}

      <SectionedSheet
        open={creating}
        title="Nueva unidad personalizada"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={() => void saveCreate()} disabled={saving || !form.label.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >{renderFields(true)}</SectionedSheet>

      <SectionedSheet
        open={editing != null}
        title="Editar unidad personalizada"
        onClose={() => { setEditing(null); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={() => void saveEdit()} disabled={saving || !form.label.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >{renderFields(false)}</SectionedSheet>

      {/* Nueva / editar equivalencia: 1 <unidad> = factor <base> */}
      <SectionedSheet
        open={convOpen}
        title="Nueva equivalencia"
        onClose={() => { setConvOpen(false); setConvError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setConvOpen(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={() => void saveConv()} disabled={convSaving || !convForm.fromUnit || !convForm.toUnit || !convForm.factor.trim()}>{convSaving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {convError && <div className="adm-banner adm-banner--danger" role="alert">{convError}</div>}
        <FormSection title="Equivalencia">
          <p className="adm-muted" style={{ margin: '0 0 4px', fontSize: 'var(--pos-text-xs, 12px)' }}>
            Declara cuánto vale tu unidad en una unidad base de su dimensión (GR para peso, ML para volumen, PZA para conteo). El motor hace el resto de conversiones.
          </p>
          <div className="adm-field">
            <span className="adm-field__label">Unidad</span>
            <UnitPicker bare value={convForm.fromUnit} onChange={setConvFrom} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--pos-space-2, 8px)', flexWrap: 'wrap' }}>
            <span className="adm-muted" style={{ paddingBottom: 12, whiteSpace: 'nowrap' }}>
              1 {convForm.fromUnit ? unitShort(convForm.fromUnit) : 'unidad'} =
            </span>
            <label className="adm-field" style={{ width: 120 }}>
              <span className="adm-field__label">Factor</span>
              <input
                className="adm-input adm-mono"
                type="text"
                inputMode="decimal"
                value={convForm.factor}
                onChange={(e) => setConvForm({ ...convForm, factor: e.target.value })}
                placeholder="ej. 1000"
                autoFocus
              />
            </label>
            <div className="adm-field" style={{ flex: 1, minWidth: 200 }}>
              <span className="adm-field__label">Unidad base / destino</span>
              <UnitPicker bare value={convForm.toUnit} onChange={(v) => setConvForm({ ...convForm, toUnit: v })} />
            </div>
          </div>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
